import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useProfile } from '../../context/ProfileContext';
import { 
  FaBan, 
  FaHeartbeat,
  FaLeaf
} from 'react-icons/fa';
import './ProfileAwarenessAlert.css';

/**
 * ProfileAwarenessAlert - Shows warnings based on user's profile
 * Checks allergies, dietary restrictions, and medical conditions
 */
function ProfileAwarenessAlert({ 
  foodName, 
  ingredients = [], 
  meal = null,
  type = 'food', // 'food' or 'meal'
  showIcon = true,
  compact = false
}) {
  const { checkAllergy, checkDietaryRestriction, checkMedicalCondition, aggregatedProfile } = useProfile();

  if (!aggregatedProfile) return null;

  const alerts = [];

  // Check allergies
  if (type === 'food') {
    const allergyCheck = checkAllergy(foodName, ingredients);
    if (allergyCheck.hasAllergy) {
      alerts.push({
        type: 'allergy',
        severity: 'critical',
        icon: <FaBan />,
        title: '⚠️ Allergy Alert',
        message: `This food contains: ${allergyCheck.allergens.join(', ')}`,
        color: '#dc3545'
      });
    }
  } else if (type === 'meal' && meal) {
    const allergyCheck = checkAllergy(meal.name || meal.title || '', meal.ingredients || []);
    if (allergyCheck.hasAllergy) {
      alerts.push({
        type: 'allergy',
        severity: 'critical',
        icon: <FaBan />,
        title: '⚠️ Allergy Alert',
        message: `This meal contains: ${allergyCheck.allergens.join(', ')}`,
        color: '#dc3545'
      });
    }
  }

  // Check dietary restrictions
  if (meal) {
    const restrictionCheck = checkDietaryRestriction(meal);
    if (restrictionCheck.violates) {
      alerts.push({
        type: 'dietary',
        severity: 'warning',
        icon: <FaLeaf />,
        title: '🌱 Dietary Restriction',
        message: `May not align with your ${aggregatedProfile.dietaryRestrictions} diet: ${restrictionCheck.reason}`,
        color: '#ffc107'
      });
    }
  }

  // Check medical conditions
  if (meal) {
    const medicalCheck = checkMedicalCondition(meal);
    if (medicalCheck.requiresAttention) {
      medicalCheck.warnings.forEach(warning => {
        alerts.push({
          type: 'medical',
          severity: 'warning',
          icon: <FaHeartbeat />,
          title: '🏥 Health Consideration',
          message: warning,
          color: '#fd7e14'
        });
      });
    }
  }

  if (alerts.length === 0) return null;

  return (
    <div className={`profile-awareness-alerts ${compact ? 'compact' : ''}`}>
      <AnimatePresence>
        {alerts.map((alert, idx) => (
          <motion.div
            key={idx}
            className={`awareness-alert alert-${alert.severity}`}
            style={{ '--alert-color': alert.color }}
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3, delay: idx * 0.1 }}
          >
            {showIcon && (
              <div className="alert-icon-wrapper" style={{ color: alert.color }}>
                {alert.icon}
              </div>
            )}
            <div className="alert-content">
              <div className="alert-title">{alert.title}</div>
              <div className="alert-message">{alert.message}</div>
            </div>
            {alert.severity === 'critical' && (
              <motion.div
                className="alert-pulse"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            )}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

/**
 * ProfileAwarenessBadge - Compact badge showing profile awareness
 */
function ProfileAwarenessBadge({ foodName, ingredients = [], meal = null, type = 'food' }) {
  const { checkAllergy, aggregatedProfile } = useProfile();

  if (!aggregatedProfile) return null;

  const hasAllergy = type === 'food' 
    ? checkAllergy(foodName, ingredients).hasAllergy
    : meal && checkAllergy(meal.name || meal.title || '', meal.ingredients || []).hasAllergy;

  if (!hasAllergy) return null;

  return (
    <motion.div
      className="profile-awareness-badge"
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      whileHover={{ scale: 1.1 }}
      title="Contains allergens from your profile"
    >
      <FaBan className="badge-icon" />
    </motion.div>
  );
}

export default ProfileAwarenessAlert;
export { ProfileAwarenessBadge };









